import type { FC, FocusEventHandler, MouseEventHandler, ReactNode } from "react";
import { useRef, useEffect } from "react";
import MuiDrawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import { Stack } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { PAGE_PADDING_Y } from "../../shared/Page";
import { customScrollbar } from "../../shared/customScrollbar";
import { SidebarMenuItem, type SidebarMenuItemType } from "./SidebarMenuItem";
import { ToggleSidebarButton } from "./ToggleSidebarButton";
import { createContext, use } from "react";
import { useLocalStorage } from "../../shared/useLocalStorage";
import { usePrevious } from "../../shared/usePrevious";
import { COLLAPSED_SIDEBAR_WIDTH, EXPANDED_SIDEBAR_WIDTH } from "./sidebarConstants";
import { Logo } from "./Logo";

type SidebarProps = {
  header?: ReactNode;
  menuItemEmphasis?: SidebarMenuItemEmphasis;
  sidebarMenuItems?: Array<SidebarMenuItemType>;
  bottomSlot?: ReactNode;
  hideToggleSidebarButton?: boolean;
};

export const Sidebar = (props: SidebarProps) => {
  return (
    <SidebarProvider menuItemEmphasis={props.menuItemEmphasis ?? "all-items"}>
      <SidebarInner {...props} />
    </SidebarProvider>
  );
};

const TOGGLE_SIDEBAR_BUTTON_ID = "ToggleSidebarButton";

const SidebarInner: FC<SidebarProps> = ({ header, sidebarMenuItems, bottomSlot, hideToggleSidebarButton = false }) => {
  const { state, isSidebarExpanded, setState, previousState } = useSidebar();

  const drawerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  const handleMouseOver: MouseEventHandler = e => {
    if ("id" in e.target && e.target.id === TOGGLE_SIDEBAR_BUTTON_ID) return;

    if (leaveTimeoutRef.current) {
      clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = null;
    }

    if (state !== "expanded") {
      hoverTimeoutRef.current = setTimeout(() => {
        setState("expanded-hover");
        hoverTimeoutRef.current = null;
      }, 50);
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    if (state === "expanded-hover") {
      leaveTimeoutRef.current = setTimeout(() => {
        setState("collapsed");
        leaveTimeoutRef.current = null;
      }, 100);
    }
  };

  const handleFocus: FocusEventHandler = () => {
    if (state !== "expanded") setState("expanded-hover");
  };

  const handleBlur: FocusEventHandler = e => {
    if (state === "expanded") return;
    const isNewTargetOutsideOfSidebar = !drawerRef.current?.contains(e.relatedTarget);
    if (isNewTargetOutsideOfSidebar) setState("collapsed");
  };

  return (
    <MuiDrawer
      ref={drawerRef}
      elevation={0}
      hideBackdrop
      onBlur={handleBlur}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      sx={{ flexShrink: 0, ...sidebarStateStyles(state, previousState) }}
      variant="permanent"
      open={isSidebarExpanded}
    >
      {header ? <Box onMouseOver={handleMouseOver}>{header}</Box> : <SidebarBrand />}
      <Stack
        sx={{ flexGrow: 1, overflowY: isSidebarExpanded ? "auto" : "hidden", overflowX: "hidden", ...customScrollbar }}
      >
        <Stack component="nav" onMouseOver={handleMouseOver} sx={{ pt: 1.5, gap: 0.5 }}>
          {sidebarMenuItems?.map(item => (
            <SidebarMenuItem key={item.variant === "custom" ? item.id : item.labelKey} item={item} />
          ))}
        </Stack>
      </Stack>
      <Box onMouseOver={handleMouseOver} sx={{ pt: 1, pb: 1 }}>
        {bottomSlot}
      </Box>
      {!hideToggleSidebarButton && (
        <ToggleSidebarButton
          sx={{ alignSelf: "end", bottom: 100, transform: "translate(50%)", position: "fixed" }}
          onChange={state => setState(state)}
          state={state}
          id={TOGGLE_SIDEBAR_BUTTON_ID}
        />
      )}
    </MuiDrawer>
  );
};
export const SidebarBrand = ({ height = 20, sx }: { height?: number; sx?: SxProps<Theme> }) => {
  const { isSidebarExpanded } = useSidebar();

  return (
    <Box component="a" href="/" sx={{ display: "block", all: "unset", cursor: "pointer" }}>
      <Box sx={{ pt: PAGE_PADDING_Y, pb: 1.5, pl: 3, color: theme => theme.palette.text.primary, ...sx }}>
        <Logo showText={isSidebarExpanded} height={height} />
      </Box>
    </Box>
  );
};

const sidebarStateStyles = (state: SidebarState, previousState: SidebarState): SxProps<Theme> => {
  const defaultPaperStyles: SxProps<Theme> = {
    borderRight: "none",
    outline: theme => `1px solid ${theme.palette.grey["300"]}`,
    backgroundColor: theme => theme.palette.grey["100"],
  };

  const expandedStyles: SxProps<Theme> = { width: EXPANDED_SIDEBAR_WIDTH, overflowX: "hidden" };

  switch (state) {
    case "expanded": {
      return { ...expandedStyles, "& .MuiDrawer-paper": { ...defaultPaperStyles, ...expandedStyles } };
    }
    case "expanded-hover": {
      const expandedHover: SxProps<Theme> = {
        ...expandedStyles,
        transition: "width 200ms ease-in",
        boxShadow: theme => `1px 0px 6px 0 ${theme.palette.grey["300"]}`,
      };
      return {
        ...expandedHover,
        "width": COLLAPSED_SIDEBAR_WIDTH,
        "& .MuiDrawer-paper": { ...defaultPaperStyles, ...expandedHover },
      };
    }
    case "collapsed": {
      const collapsedStyles: SxProps<Theme> = {
        width: COLLAPSED_SIDEBAR_WIDTH,
        overflowX: "hidden",
        transition: previousState === "expanded-hover" ? "width 100ms ease-out" : "none",
      };
      return { ...collapsedStyles, "& .MuiDrawer-paper": { ...defaultPaperStyles, ...collapsedStyles } };
    }
  }
};

export type SidebarState = "expanded" | "expanded-hover" | "collapsed";

/** TODO: temporary — remove once we decide which emphasis to keep. */
export type SidebarMenuItemEmphasis = "all-items" | "active-item-only";

type Sidebar = {
  isSidebarExpanded: boolean;
  menuItemEmphasis: SidebarMenuItemEmphasis;
  state: SidebarState;
  previousState: Sidebar["state"];
  setState: (state: Sidebar["state"]) => void;
};

const SidebarContext = createContext<Sidebar | null>(null);

const SidebarProvider = ({
  children,
  menuItemEmphasis,
}: {
  children: ReactNode;
  menuItemEmphasis: SidebarMenuItemEmphasis;
}) => {
  const [state, setState] = useLocalStorage<Sidebar["state"]>(
    "privacy:sidebar-state",
    item => item === "expanded" || item === "collapsed",
    "expanded"
  );
  const previousState = usePrevious(state) ?? state;

  const isSidebarExpanded = state !== "collapsed";

  return (
    <SidebarContext value={{ state, setState, previousState, isSidebarExpanded, menuItemEmphasis }}>
      {children}
    </SidebarContext>
  );
};

export const useSidebar = () => {
  const context = use(SidebarContext);
  if (!context) throw new Error('"useSidebar" must be used within a SidebarStateProvider');

  return context;
};
